<template>
  <div
    class="Component__TemplateMasonry relative"
    :style="{
      height: rem ? pxToRem(maxHeight) : maxHeight + 'px',
      willChange: 'transform',
      transform: 'translate3d(0, 0, 0)',
    }"
  >
    <template v-if="list.length > 0">
      <div
        v-for="(item, idx) in displayList"
        :key="item ? item.id : idx"
        :class="getCardClass(item, idx)"
        :style="getCardStyle(item, idx)"
        :data-tid="item ? `t_${item.id}` : idx"
      >
        <CardItem
          v-if="item"
          :data="item"
          :auto-scale="true"
          :source="template_source"
          @change="onChange($event, item)"
          @click="onHandleClick(item)"
        >
          {{ item.name }}
        </CardItem>
      </div>
    </template>
    <EmptyView v-else-if="!loading" :style="{ background: 'none' }"></EmptyView>
    <Scenarios v-if="showScenario" />
  </div>
</template>

<script setup lang="ts">
import Scenarios from './scenarios.vue'
import EmptyView from '~/components/emptyView/index.vue'
import type { Template } from '~/api/interface'
import { useTemplateDetail } from '~/hooks/useModal/modals/template'
import CardItem from '~/features/template/components/cardItem.vue'
import { TRACK_EVENT, TEMPLATE_SOURCE } from '~/utils/track'
import { getTagIDs } from '~/features/template/components/info'

const props = withDefaults(
  defineProps<{
    showScenario?: boolean
    list: Template[]
    loading?: boolean
    rem?: boolean
    columns?: number
    template_source: TEMPLATE_SOURCE
    onCardClick?: (data: Template) => void
  }>(),
  {
    columns: 5,
    loading: false,
    width: undefined,
    rem: true,
  }
)

const CardTypeStyle = {
  vertical: {
    width: 230,
    height: 472,
  },
  horizontal: {
    width: 230,
    height: 189,
  },
  gap: 12,
}

const maxHeight = ref(0)

const positionArray = computed(() => {
  maxHeight.value = 0
  const columnsDepth = new Array(props.columns).fill(0)
  return displayList.value.map((item, idx) => {
    const col = columnsDepth.indexOf(Math.min(...columnsDepth))
    const y = columnsDepth[col]
    const x = col * (CardTypeStyle.vertical.width + CardTypeStyle.gap)
    if (item?.resolution === '1080x1920') {
      columnsDepth[col] += CardTypeStyle.vertical.height + CardTypeStyle.gap
    } else {
      columnsDepth[col] += CardTypeStyle.horizontal.height + CardTypeStyle.gap
    }
    maxHeight.value = Math.max(maxHeight.value, columnsDepth[col])
    return { x, y }
  })
})

const displayList = computed(() => {
  if (props.showScenario) {
    return [null, null, ...props.list]
  }
  return props.list
})

const getCardClass = (item: Template | null, idx: number) => {
  return {
    'masonry-item': true,
    absolute: true,
    vertical: item?.resolution === '1080x1920',
    horizontal: item?.resolution === '1920x1080',
  }
}

const getCardStyle = (item: Template | null, idx: number) => {
  const style: Record<string, string> = {}
  const { x, y } = positionArray.value[idx]
  const isVertical = item?.resolution === '1080x1920'
  const width = isVertical
    ? CardTypeStyle.vertical.width
    : CardTypeStyle.horizontal.width
  const height = isVertical
    ? CardTypeStyle.vertical.height
    : CardTypeStyle.horizontal.height

  const displayX = props.rem ? pxToRem(x) : x + 'px'
  const displayY = props.rem ? pxToRem(y) : y + 'px'
  const displayWidth = props.rem ? pxToRem(width) : width + 'px'
  const displayHeight = props.rem ? pxToRem(height) : height + 'px'

  style.left = displayX
  style.top = displayY
  style.width = displayWidth
  style.height = displayHeight
  style.containIntrinsicSize = `${displayWidth} ${displayHeight}`
  style.pointerEvents = item ? 'auto' : 'none'

  return style
}

const onChange = ([key, value]: [string, any], data: Template) => {
  const item: any = props.list.find((item) => {
    return item.id === data.id
  })
  if (item) {
    item[key] = value
  }
}

const resourceLocation = injectResourceLocation()
const userInfo = useUserInfo()

const onHandleClick = (data: Template) => {
  const trackParams = {
    template_id: data.id,
    template_title: data.name,
    source: resourceLocation?.source,
    tags: getTagIDs(data.tag_ids).map((item) => item.tag_name),
    org_id: userInfo.value?.organization_id,
  }

  if (props.template_source === TEMPLATE_SOURCE.BEST_PRACTICE_PAGE) {
    trackEvent(TRACK_EVENT.CLICK_EXCELLENT_WORK, trackParams)
  } else {
    trackEvent(TRACK_EVENT.CLICK_TEMPLATE_PREVIEW, trackParams)
  }

  if (props.onCardClick) {
    props.onCardClick(data)
  } else {
    useTemplateDetail(
      props.list,
      data,
      {
        templateSource: props.template_source,
        source: resourceLocation?.source,
      },
      onChange
    )
  }
}
</script>
